import type { PAGE_POSITIONS, PageType } from 'types';
import type { ProductListItem } from 'types/capi/shared';
import type { User } from 'types/User';

import algolia from '~/services/algolia.client';
import gtm from '~/services/gtm.client';
import {
  getEntrypoint,
  getPreviousPagePath,
  getPreviousPagePosition,
  getSourceAndMedium,
  setNavigationContext
} from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  isBot: boolean;
  isClickOut: boolean;
  listId?: string;
  pagePosition: PAGE_POSITIONS;
  pageType?: PageType;
  position?: number;
  product: ProductListItem;
  user: User;
};

class ProductCardClickEvent extends UserEvent<EventData> {
  sendToAlgolia() {
    if (!this.data.product.queryID) return;
    if (!this.data.product.position) return;

    algolia.clickedObjectIdAfterSearch({
      id: this.data.product.id.toString(),
      position: this.data.product.position,
      queryID: this.data.product.queryID,
      user: this.data.user
    });
  }

  private getPrice() {
    return (
      this.data.product.price.sale ??
      this.data.product.price.regular ??
      undefined
    );
  }

  private getPayload() {
    return {
      entry_point: getEntrypoint(),
      is_click_out: this.data.isClickOut,
      item_list_name: window.location.pathname + window.location.search,
      list_id: this.data.listId ?? null,
      page_position: this.data.pagePosition,
      platform: 'web' as const,
      position: this.data.position,
      previous_page_path: getPreviousPagePath(),
      previous_page_position: getPreviousPagePosition(),
      product_brand: this.data.product.brand,
      product_id: this.data.product.id,
      product_name: this.data.product.name,
      product_view_price: this.getPrice(),
      source_medium: getSourceAndMedium(),
      stock_status: this.data.product.stock_status,
      style_code: this.data.product.style_code
    };
  }

  sendToGtm() {
    return gtm.productCardClick(this.getPayload());
  }

  collectServerEvents() {
    setNavigationContext({
      listId: this.data.listId,
      pagePosition: this.data.pagePosition,
      position: this.data.position
    });

    this.serverEvents.push({
      data: {
        ...this.getPayload(),
        is_logged_in: this.data.user.isLoggedIn,
        page_path: window.location.pathname,
        page_type: this.data.pageType,
        platform: 'web' as const
      },
      type: 'product_card_click'
    });
  }
}

export default ProductCardClickEvent;
